import { useState } from "react";
import { toast } from "sonner";
import { useCard, qp } from "@/components/perf/common";

const KIND = { strength: ["Strength", "var(--ok)"], gap: ["To improve", "var(--wa)"], outside_control: ["Outside your control", "var(--ac)"], attention: ["Needs attention", "var(--er)"], no_data: ["No data", "var(--ng)"], data_issue: ["Data issue", "var(--ng)"] };

function AiNote({ P, userId, saved }) {
  const [txt, setTxt] = useState(saved?.text || "");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true); setTxt("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/perf/coach/explain`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...qp(P), user_id: userId || null }) });
      if (!res.ok) throw new Error((await res.json()).detail || "Coach unavailable");
      const rd = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await rd.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) { const j = JSON.parse(p.replace(/^data: /, "")); if (j.t) setTxt((x) => x + j.t); if (j.error) toast.error(j.error); }
      }
    } catch (e) { toast.error(String(e.message || e)); }
    setBusy(false);
  };
  return (<section className="box" style={{ padding: "14px 16px", margin: "12px 0" }} data-testid="coach-ai">
    <div className="r sp"><b>Coach explanation</b><button className="b sm" disabled={busy} onClick={run} data-testid="coach-explain-btn">{busy ? "Writing…" : txt ? "Explain again" : "Explain my results"}</button></div>
    <p className="mu" style={{ fontSize: 12 }}>Written by AI from the verified numbers below. It never changes scores or records.{saved && !busy ? ` Last written ${saved.at.slice(0, 16).replace("T", " ")}.` : ""}</p>
    {txt && <div style={{ whiteSpace: "pre-wrap", fontSize: 13.5 }} data-testid="coach-ai-text">{txt.replace(/^#+\s*/gm, "").replace(/\*\*(.+?)\*\*/g, "$1")}</div>}
  </section>);
}

export default function Coach({ P, userId }) {
  const d = useCard("/perf/coach", P, userId);
  if (!d) return <p className="mu">Loading…</p>;
  if (d.error) return <p className="mu">Could not load coaching.</p>;
  return (
    <div data-testid="coach">
      <p className="mu">{d.role.label} · {d.from} to {d.to} · role score {d.score ?? "N/A"}</p>
      <AiNote P={P} userId={userId} saved={d.ai_note} />
      {!d.findings.length && <p className="mu" data-testid="coach-empty">No findings yet. Findings appear once the role has KPIs and there is recorded work in the period.</p>}
      {d.findings.map((f, i) => { const k = KIND[f.kind]; return (
        <section key={i} className="box" style={{ padding: "12px 16px", marginBottom: 8 }} data-testid={`coach-finding-${f.kind}`}>
          <div><span className="chip" style={{ "--c": k[1] }}>{k[0]}</span> <b>{f.label}</b></div>
          {f.text && <p className="mu" style={{ margin: "4px 0" }}>{f.text}</p>}
          {f.actual !== undefined && <p className="mu" style={{ margin: "4px 0", fontSize: 12.5 }}>Actual {f.actual ?? "N/A"} vs target {f.target}{f.calc ? ` (${f.calc})` : ""}</p>}
          {f.suggested_actions?.length > 0 && <><small className="mu">Suggested actions (not V.A policy):</small><ul style={{ margin: "2px 0", paddingLeft: 18, fontSize: 13 }}>{f.suggested_actions.map((a) => <li key={a}>{a}</li>)}</ul></>}
          {f.evidence?.length > 0 && <><small className="mu">Supporting records:</small>{f.evidence.map((e) => <div key={e.id} className="mu" style={{ fontSize: 12 }}>• {e.title}{e.company ? ` · ${e.company}` : ""}{e.deadline ? ` · deadline ${e.deadline}` : ""}{e.completed ? ` · completed ${e.completed}` : ""}{e.blocked_by ? ` · ${e.blocked_by}` : ""}{e.reason ? ` · "${e.reason}"` : ""}</div>)}</>}
        </section>); })}
      {d.resources.length > 0 && <><h4>Learning material for this role</h4>{d.resources.map((r) => <div key={r.id} className="mu">• {r.title} ({r.kind})</div>)}</>}
      {d.resource_gaps.map((g) => <p key={g.metric} className="note" data-testid={`coach-gap-${g.metric}`}>{g.text}</p>)}
    </div>);
}
